import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy
} from '@angular/core';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { format, parseISO } from 'date-fns';

import { SondermindEvent, SondermindEvents } from '@sondermind/analytics';
import {
  ClientInsuranceHttpService,
  IBvDemographicInformation,
  IBvSuggestedPayer,
  IPatientInsuranceWithBvInfo
} from '@sondermind/data-access/client-insurance';
import { NotificationToastService, ToastType } from '@sondermind/features/notification-toast';
import {
  IrisButtonTypes,
  IrisButtonContainerMobileLayouts,
  IrisButtonContainerDesktopLayouts
} from '@sondermind/iris/button';
import { IconSizes } from '@sondermind/iris/icon';
import { SegmentService } from '@sondermind/segment';

@Component({
  selector: 'client-demographic-update',
  templateUrl: './demographic-update.component.html',
  styleUrls: ['./demographic-update.component.scss'],
  standalone: false
})
export class DemographicUpdateComponent implements OnInit, OnDestroy {
  @Input() insuranceResponse: IPatientInsuranceWithBvInfo = null;
  @Input() isMobile: boolean = false;
  @Input() useInline: boolean = false;
  @Input() providerNetworkCompany: any = null;
  @Input() userFormValues: any = null;

  @Output() closeUpdate = new EventEmitter<{
    insuranceUpdated: boolean;
    bvError: boolean;
    demographicUpdated?: boolean;
    insuranceAddedId?: number;
  }>();
  @Output() hideParentContent = new EventEmitter<boolean>();

  processing: boolean = false;
  supportContactUrl: string = 'https://www.sondermind.com/contact-us/';

  private destroyed$ = new Subject<void>();

  IrisButtonTypes = IrisButtonTypes;
  IrisButtonContainerMobileLayouts = IrisButtonContainerMobileLayouts;
  IrisButtonContainerDesktopLayouts = IrisButtonContainerDesktopLayouts;
  IconSizes = IconSizes;

  constructor(
    private clientInsuranceHttpService: ClientInsuranceHttpService,
    private notificationToastService: NotificationToastService,
    private segmentService: SegmentService<SondermindEvents>
  ) {}

  ngOnInit(): void {
    this.segmentService.track<SondermindEvent.DemographicUpdateSuggestionsDisplayed>(
      SondermindEvent.DemographicUpdateSuggestionsDisplayed,
      {
        personaClientId: this.insuranceResponse.personaClientId,
        patientInsuranceId: this.insuranceResponse.id,
        providerNetworkCompanyId: this.providerNetworkCompany?.id,
        hadSuggestedPayer: this.isInsuranceNameDifferent,
        suggestedPayerId: this.suggestedPayer?.id
      }
    );
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  get demographicInfo(): IBvDemographicInformation {
    return this.insuranceResponse?.recentBenefitVerification?.demographicInformation;
  }

  get insuranceName(): string {
    return this.providerNetworkCompany?.name || 'Your insurance';
  }

  get suggestedPayer(): IBvSuggestedPayer {
    return this.insuranceResponse?.recentBenefitVerification?.suggestedPayer || null;
  }

  get suggestedInsuranceName(): string {
    return this.suggestedPayer?.name || this.insuranceName;
  }

  get isInsuranceNameDifferent(): boolean {
    if (!this.suggestedPayer || !this.providerNetworkCompany) {
      return false;
    }

    const currentInsuranceName = this.providerNetworkCompany.name?.trim()?.toLowerCase();
    const suggestedInsuranceName = this.suggestedPayer.name?.trim()?.toLowerCase();

    return currentInsuranceName && suggestedInsuranceName && currentInsuranceName !== suggestedInsuranceName;
  }

  get memberName(): string {
    const demo = this.demographicInfo;
    if (demo?.firstName && demo?.lastName) {
      return `${demo.firstName} ${demo.lastName}`;
    }
    return '-';
  }

  get dateOfBirth(): string {
    const dateValue = this.demographicInfo?.dateOfBirth || this.demographicInfo?.dob;
    if (dateValue) {
      try {
        const date = parseISO(dateValue);
        return format(date, 'MMM d, yyyy');
      } catch (error) {
        return dateValue;
      }
    }
    return '-';
  }

  get memberId(): string {
    return this.demographicInfo?.policyNumber || '-';
  }

  get groupNumber(): string {
    return this.insuranceResponse?.insuranceInformation?.groupNumber || '-';
  }

  get isNameDifferent(): boolean {
    if (!this.userFormValues || !this.demographicInfo) {
      return false;
    }

    const userFirstName = this.userFormValues.first_name?.trim()?.toLowerCase();
    const userLastName = this.userFormValues.last_name?.trim()?.toLowerCase();
    const payorFirstName = this.demographicInfo.firstName?.trim()?.toLowerCase();
    const payorLastName = this.demographicInfo.lastName?.trim()?.toLowerCase();

    return (userFirstName && payorFirstName && userFirstName !== payorFirstName) ||
           (userLastName && payorLastName && userLastName !== payorLastName);
  }

  get isDateOfBirthDifferent(): boolean {
    if (!this.userFormValues || !this.demographicInfo) {
      return false;
    }

    const userDob = this.userFormValues.date_of_birth;
    const payorDob = this.demographicInfo.dateOfBirth || this.demographicInfo.dob;

    if (!userDob || !payorDob) {
      return false;
    }

    return !this.datesMatch(userDob, payorDob);
  }

  get isMemberIdDifferent(): boolean {
    if (!this.userFormValues || !this.demographicInfo) {
      return false;
    }

    const userPolicyNumber = this.userFormValues.policy_number?.trim();
    const payorPolicyNumber = this.demographicInfo.policyNumber || this.demographicInfo.memberId;

    if (!userPolicyNumber || !payorPolicyNumber) {
      return false;
    }

    return userPolicyNumber !== payorPolicyNumber;
  }

  private datesMatch(userDate: string, payorDate: string): boolean {
    try {
      let userIso: string;
      let payorIso: string;

      if (userDate.includes('/')) {
        const [month, day, year] = userDate.split('/');
        userIso = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      } else {
        userIso = userDate;
      }

      if (payorDate.includes('/')) {
        const [month, day, year] = payorDate.split('/');
        payorIso = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      } else {
        payorIso = payorDate;
      }

      return userIso === payorIso;
    } catch (error) {
      return userDate === payorDate;
    }
  }

  onContinueWithUpdatedInfo(): void {
    if (!this.insuranceResponse?.id) {
      this.notificationToastService.showNotificationToast({
        message: 'Error updating insurance information',
        type: ToastType.ERROR
      });
      return;
    }

    this.processing = true;

    const updateData: any = {
      skip_benefit_verification: true,
      provider_network_company_id: this.suggestedPayer?.id || this.providerNetworkCompany?.id,
      group_number: this.insuranceResponse?.insuranceInformation?.groupNumber,
      policy_number: this.demographicInfo?.policyNumber || this.demographicInfo?.memberId,
      first_name: this.demographicInfo?.firstName,
      last_name: this.demographicInfo?.lastName,
      date_of_birth: this.demographicInfo?.dateOfBirth || this.demographicInfo?.dob,
      gender: this.demographicInfo?.gender,
      address_line1: this.demographicInfo?.addressLine1,
      address_line2: this.demographicInfo?.addressLine2,
      city: this.demographicInfo?.city,
      state: this.demographicInfo?.state,
      postal_code: this.demographicInfo?.postalCode
    };

    this.clientInsuranceHttpService.update(this.insuranceResponse.id, updateData)
      .pipe(
        finalize(() => {
          this.processing = false;
        }),
        takeUntil(this.destroyed$)
      )
      .subscribe({
        next: (resp) => {
          if (!this.useInline) {
            this.notificationToastService.showNotificationToast({
              message: 'Insurance information updated',
              type: ToastType.SUCCESS
            });
          }

          this.segmentService.track<SondermindEvent.DemographicUpdateAccepted>(
            SondermindEvent.DemographicUpdateAccepted,
            {
              personaClientId: this.insuranceResponse.personaClientId,
              patientInsuranceId: this.insuranceResponse.id,
              providerNetworkCompanyId: this.providerNetworkCompany?.id,
              hadSuggestedPayer: this.isInsuranceNameDifferent,
              suggestedPayerId: this.suggestedPayer?.id,
              accepted: true
            }
          );

          this.closeUpdate.emit({
            insuranceUpdated: true,
            bvError: false,
            demographicUpdated: true,
            insuranceAddedId: resp?.id
          });
        },
        error: (err) => {
          this.notificationToastService.showNotificationToast({
            message: 'Error updating insurance information',
            type: ToastType.ERROR
          });

          // Even on error, emit to allow user to continue
          this.closeUpdate.emit({
            insuranceUpdated: false,
            bvError: true,
            demographicUpdated: false,
            insuranceAddedId: this.insuranceResponse?.id
          });
        }
      });
  }
}
